/* eslint-disable max-lines */
import {
  MotionDetectionWindow,
  SiteSchedule,
  TimeRange,
} from '@hakimo-ui/hakimo/types';
import {
  WEEKDAYS,
  isValidTime,
  splitDailyWindow,
} from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getSchedulesFromPayload } from '../schedule/siteSchedule';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

export function getSchedulesFromTimeRange(
  timeRange?: TimeRange
): SiteSchedule[] {
  if (
    !timeRange ||
    !isValidTime(timeRange.startTime) ||
    !isValidTime(timeRange.endTime)
  ) {
    return [];
  }
  return [
    {
      ranges: [
        {
          startTime: timeRange.startTime,
          endTime: timeRange.endTime,
          order: 0,
        },
      ],
      enabledDays: new Array(7).fill(true),
    },
  ];
}

const hasUsableRange = (schedule: SiteSchedule): boolean =>
  schedule.enabledDays.some(Boolean) && schedule.ranges.length > 0;

export function normalizeSchedules(situation: {
  schedules?: MotionDetectionWindow;
}): SiteSchedule[] {
  if (!situation.schedules) return [];
  return getSchedulesFromPayload(situation.schedules)
    .map((schedule) => ({
      ...schedule,
      ranges: schedule.ranges.filter(
        (range) => isValidTime(range.startTime) && isValidTime(range.endTime)
      ),
    }))
    .filter(hasUsableRange);
}

interface Occurrence {
  startEpochMs: number;
  endEpochMs: number;
}

const NEAR_WINDOW_MS = 60 * 60 * 1000;

function getOccurrences(
  schedule: SiteSchedule,
  alarmStartEpochMs: number,
  timezone: string
): Occurrence[] {
  const firstDay = dayjs(alarmStartEpochMs)
    .tz(timezone)
    .startOf('day')
    .subtract(1, 'day');
  const occurrences: Occurrence[] = [];

  for (let offset = 0; offset < 3; offset++) {
    const day = firstDay.add(offset, 'day');
    if (!schedule.enabledDays[day.day()]) continue;

    schedule.ranges.forEach((range) => {
      splitDailyWindow(range.startTime, range.endTime).forEach((segment) => {
        const anchor = segment.isOverflow ? day.add(1, 'day') : day;
        occurrences.push({
          startEpochMs: anchor.add(segment.startSec, 'second').valueOf(),
          endEpochMs: anchor.add(segment.endSec, 'second').valueOf(),
        });
      });
    });
  }

  return occurrences;
}

export function getRelevantSchedules(
  situation: { schedules?: MotionDetectionWindow },
  alarmStartEpochMs: number | undefined,
  alarmEndEpochMs: number | undefined,
  locationTimezone: string
): SiteSchedule[] {
  const schedules = normalizeSchedules(situation);
  if (!schedules.length || !alarmStartEpochMs) return schedules;

  const timezone = situation.schedules?.timezone || locationTimezone || 'UTC';
  const alarmEnd = Math.max(
    alarmEndEpochMs ?? alarmStartEpochMs,
    alarmStartEpochMs
  );
  const perSchedule = schedules.map((schedule) =>
    getOccurrences(schedule, alarmStartEpochMs, timezone)
  );

  const containing = schedules.filter((_, i) =>
    perSchedule[i].some(
      (occurrence) =>
        occurrence.startEpochMs <= alarmEnd &&
        alarmStartEpochMs <= occurrence.endEpochMs
    )
  );
  if (containing.length) return containing;

  const imminent = schedules.filter((_, i) =>
    perSchedule[i].some(
      (occurrence) =>
        occurrence.startEpochMs > alarmEnd &&
        occurrence.startEpochMs - alarmEnd <= NEAR_WINDOW_MS
    )
  );

  return imminent.length ? imminent : schedules;
}

const MON_FIRST = [1, 2, 3, 4, 5, 6, 0];
const abbrev = (i: number) =>
  WEEKDAYS[i][0] + WEEKDAYS[i].slice(1, 3).toLowerCase();

function compressDays(enabledDays: boolean[]): string {
  const active = MON_FIRST.map((wi, pos) => ({ pos, wi })).filter(
    ({ wi }) => enabledDays[wi]
  );

  const ranges = active.reduce<{ pos: number; wi: number }[][]>(
    (acc, day, i) => {
      if (i > 0 && day.pos === active[i - 1].pos + 1) {
        acc[acc.length - 1].push(day);
      } else {
        acc.push([day]);
      }
      return acc;
    },
    []
  );

  return ranges
    .map((g) =>
      g.length >= 3
        ? `${abbrev(g[0].wi)}–${abbrev(g[g.length - 1].wi)}`
        : g.map((d) => abbrev(d.wi)).join(', ')
    )
    .join(', ');
}

const formatTime = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export function getScheduleLabels(schedules: SiteSchedule[]): string[] {
  return schedules.flatMap((schedule) =>
    schedule.ranges.map((range) => {
      const dayLabel = schedule.enabledDays.every(Boolean)
        ? 'Every day'
        : compressDays(schedule.enabledDays);
      const timeLabel = `${formatTime(range.startTime)}–${formatTime(
        range.endTime
      )}`;

      return `${dayLabel} · ${timeLabel}`;
    })
  );
}
